export interface FontOption {
  id: string
  label: string
  stack: string
  weight?: number
  google?: string
}

const AVENIR_STACK = "'Avenir Next', 'Avenir', 'Nunito Sans', Helvetica, Arial, sans-serif"

export const FONT_OPTIONS: FontOption[] = [
  {
    id: 'avenir',
    label: 'Avenir',
    stack: AVENIR_STACK,
    weight: 400,
    google: 'Nunito+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400',
  },
  {
    id: 'avenir-medium',
    label: 'Avenir Medium',
    stack: AVENIR_STACK,
    weight: 500,
    google: 'Nunito+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400',
  },
  {
    id: 'avenir-light',
    label: 'Avenir Light',
    stack: AVENIR_STACK,
    weight: 300,
    google: 'Nunito+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400',
  },
  {
    id: 'roboto',
    label: 'Roboto',
    stack: "'Roboto', Helvetica, Arial, sans-serif",
    google: 'Roboto:ital,wght@0,300;0,400;0,500;0,700;0,900;1,300;1,400;1,500;1,700',
  },
  {
    id: 'helvetica-neue',
    label: 'Helvetica Light',
    stack: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  {
    id: 'helvetica',
    label: 'Helvetica',
    stack: 'Helvetica, Arial, sans-serif',
  },
  {
    id: 'inter',
    label: 'Inter',
    stack: "'Inter', Helvetica, Arial, sans-serif",
    google: 'Inter:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400',
  },
  {
    id: 'garamond',
    label: 'EB Garamond',
    stack: "'EB Garamond', Garamond, 'Times New Roman', serif",
    google: 'EB+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500',
  },
  {
    id: 'playfair',
    label: 'Playfair Display',
    stack: "'Playfair Display', Georgia, serif",
    google: 'Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400',
  },
]

export const DEFAULT_HEADING_FONT = 'avenir-medium'
export const DEFAULT_BODY_FONT = 'avenir-light'

/* -------------------------------------------------------------------------- */
/*                              Graisses (weights)                            */
/* -------------------------------------------------------------------------- */

export const FONT_WEIGHT_OPTIONS = [
  { id: 'light', label: 'Light', value: 300 },
  { id: 'regular', label: 'Regular', value: 400 },
  { id: 'medium', label: 'Medium', value: 500 },
  { id: 'semibold', label: 'Semi-bold', value: 600 },
  { id: 'bold', label: 'Bold', value: 700 },
] as const

export type FontWeightId = (typeof FONT_WEIGHT_OPTIONS)[number]['id']

export type FontWeightKey =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'body'
  | 'nav'
  | 'button'
  | 'quote'
  | 'small'

export type FontWeights = Record<FontWeightKey, FontWeightId>

export const FONT_WEIGHT_FIELDS: { key: FontWeightKey; label: string; hint: string }[] = [
  { key: 'h1', label: 'Titres principaux (H1)', hint: 'Grands titres de page et hero' },
  { key: 'h2', label: 'Titres de section (H2)', hint: 'Titres de blocs et sections' },
  { key: 'h3', label: 'Sous-titres (H3)', hint: 'Titres de cartes et petites sections' },
  { key: 'body', label: 'Texte courant', hint: 'Paragraphes et descriptions' },
  { key: 'nav', label: 'Navigation', hint: 'Menu, liens du header et du footer' },
  { key: 'button', label: 'Boutons', hint: "Boutons d'action et formulaires" },
  { key: 'quote', label: 'Citations', hint: 'Citations et accroches mises en avant' },
  { key: 'small', label: 'Petits textes', hint: 'Légendes, mentions, libellés' },
]

export const DEFAULT_FONT_WEIGHTS: FontWeights = {
  h1: 'medium',
  h2: 'medium',
  h3: 'medium',
  body: 'light',
  nav: 'medium',
  button: 'medium',
  quote: 'light',
  small: 'regular',
}

export function getFontWeightValue(id?: string | null): number {
  return FONT_WEIGHT_OPTIONS.find((option) => option.id === id)?.value ?? 400
}

export function normalizeFontWeights(input?: Partial<FontWeights> | null): FontWeights {
  return FONT_WEIGHT_FIELDS.reduce((acc, field) => {
    const candidate = input?.[field.key]
    acc[field.key] = FONT_WEIGHT_OPTIONS.some((option) => option.id === candidate)
      ? (candidate as FontWeightId)
      : DEFAULT_FONT_WEIGHTS[field.key]
    return acc
  }, {} as FontWeights)
}

export function getFontById(id?: string | null): FontOption {
  return FONT_OPTIONS.find((font) => font.id === id) || FONT_OPTIONS[0]
}

export function buildGoogleFontsHref(headingId: string, bodyId: string): string {
  const families = [headingId, bodyId]
    .map((id) => getFontById(id).google)
    .filter((value, index, list): value is string => Boolean(value) && list.indexOf(value) === index)

  if (families.length === 0) return ''
  return `https://fonts.googleapis.com/css2?${families.map((family) => `family=${family}`).join('&')}&display=swap`
}

export function applySiteFonts(
  headingId: string,
  bodyId: string,
  weights?: Partial<FontWeights> | null,
) {
  const root = document.documentElement
  const heading = getFontById(headingId)
  const body = getFontById(bodyId)
  const resolvedWeights = normalizeFontWeights(weights)

  root.style.setProperty('--font-heading', heading.stack)
  root.style.setProperty('--font-body', body.stack)
  root.style.setProperty('--font-heading-weight', String(heading.weight ?? 500))
  root.style.setProperty('--font-body-weight', String(body.weight ?? 300))

  FONT_WEIGHT_FIELDS.forEach(({ key }) => {
    root.style.setProperty(`--font-weight-${key}`, String(getFontWeightValue(resolvedWeights[key])))
  })

  root.setAttribute('data-heading-font', headingId)
  root.setAttribute('data-body-font', bodyId)
}
